import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import LessonView from "@/pages/LessonView";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import Leaderboard from "@/pages/Leaderboard";
import Certificate from "@/pages/Certificate";
import ProgramPage from "@/pages/ProgramPage";
import Articles from "@/pages/Articles";
import Mentorship from "@/pages/Mentorship";
import B2B from "@/pages/B2B";
import Team from "@/pages/Team";
import { ScrollToTop } from "@/components/ScrollToTop";
import VerifyCertificate from "@/pages/VerifyCertificate";
import Academy from "@/pages/Academy";
import AcademyCourse from "@/pages/AcademyCourse";
import AcademyLesson from "@/pages/AcademyLesson";
import AcademyQuiz from "@/pages/AcademyQuiz";
import Certificates from "@/pages/Certificates";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ArticleDetail from "@/pages/ArticleDetail";
import Studio from "@/pages/Studio";
import OfferBanner from "@/components/OfferBanner";
import Diplomas from "@/pages/Diplomas";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";

const Loader = () => (
  <div className="min-h-[60vh] flex items-center justify-center" data-testid="app-loader">
    <div className="w-10 h-10 rounded-full border-4 border-[#E2E8F0] border-t-[#1D4ED8] animate-spin" />
  </div>
);

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <div className="min-h-screen bg-white text-[#0B1B3A]">
            <OfferBanner />
            <Navbar />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Auth mode="login" />} />
              <Route path="/register" element={<Auth mode="register" />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/courses" element={<Courses />} />
              <Route path="/courses/:courseId" element={<CourseDetail />} />
              <Route path="/lessons/:lessonId" element={<Protected><LessonView /></Protected>} />
              <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
              <Route path="/programs/:courseId" element={<ProgramPage />} />
              <Route path="/articles" element={<Articles />} />
              <Route path="/articles/:slug" element={<ArticleDetail />} />
              <Route path="/academy" element={<Navigate to="/courses?tab=online" replace />} />
              <Route path="/academy/:slug" element={<AcademyCourse />} />
              <Route path="/academy/:slug/quiz" element={<Protected><AcademyQuiz /></Protected>} />
              <Route path="/academy/lesson/:lessonId" element={<Protected><AcademyLesson /></Protected>} />
              <Route path="/certificates" element={<Protected><Certificates /></Protected>} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/learn" element={<Navigate to="/academy" replace />} />
              <Route path="/learn/:courseId" element={<Navigate to="/academy" replace />} />
              <Route path="/mentorship" element={<Mentorship />} />
              <Route path="/diplomas" element={<Diplomas />} />
              <Route path="/b2b" element={<B2B />} />
              <Route path="/studio" element={<Studio />} />
              <Route path="/team" element={<Team />} />
              <Route path="/verify/:serial" element={<VerifyCertificate />} />
              <Route path="/leaderboard" element={<Protected><Leaderboard /></Protected>} />
              <Route path="/certificate/:courseId" element={<Protected><Certificate /></Protected>} />
              <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
